import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHttpClient } from "shared/hooks/http-hook";
import { useForm } from "shared/hooks/form-hook";
import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";

import Input from "shared/components/FormElements/Input";
import CustomSelect from "shared/components/FormElements/Select";
import FormFooter from "shared/components/FormElements/FormFooter";
import GenericCard from "components/ui/cards/GenericCard";
import NotFound from "shared/components/UIElements/NotFound";
import ItemGenerated from "components/shared/ItemGenerated";
import DeleteModal from "components/shared/DeleteModal";
import { StoreContext } from "shared/context/store-context";
import DatePicker from "shared/components/FormElements/DatePicker";
import { parseComplexDate } from "shared/util/util";

import "./StockMovements.css";

const StockMovementItem = ({ itemId, isNewItem, DeleteItem, InsertItem, UpdateItem, onClose, gotoItem }) => {
  const { t } = useTranslation();
  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Additional states for assignee handling
  const [currentAssigneeType, setCurrentAssigneeType] = useState("attendant");
  const [currentAssigneeId, setCurrentAssigneeId] = useState(null);
  const [currentAssigneeName, setCurrentAssigneeName] = useState("");
  const [currentVehicleKilometer, setCurrentVehicleKilometer] = useState("");

  const { stockOptions, RetrieveStocks, stockMovements, attendants, drivers, vehicleOptions, thirdParties } =
    useContext(StoreContext);

  // Form state using useForm hook
  const [formState, inputHandler] = useForm(
    {
      stock: { value: "", isValid: false },
      movementType: { value: "in", isValid: true },
      quantity: { value: "", isValid: false },
      description: { value: "", isValid: true },
      referenceNumber: { value: "", isValid: true },
      date: { value: "", isValid: true },
      assigneeName: { value: "", isValid: true },
      thirdParty: { value: "", isValid: true },
      cost: { value: "", isValid: true },
    },
    false
  );

  const thirdPartyOptions = useMemo(
    () =>
      (thirdParties || []).map((partner) => ({
        value: partner.id,
        label: partner.name,
      })),
    [thirdParties]
  );

  // Movement type options
  const movementTypeOptions = [
    { value: "in", label: t("inventoryStockMovement.movementType.in") },
    { value: "out", label: t("inventoryStockMovement.movementType.out") },
  ];

  // Fetch item data
  const fetchItem = useCallback(async () => {
    setIsItemLoaded(false);
    try {
      const currentItem = stockMovements.find((item) => item.id === itemId);
      setLoadedItem(currentItem);

      if (currentItem) {
        setCurrentAssigneeType(currentItem.assigneeType || "attendant");
        setCurrentAssigneeId(currentItem.assigneeId || null);
        setCurrentAssigneeName(currentItem.assigneeName || "");
        setCurrentVehicleKilometer(currentItem.vehicleKilometer || "");
      }
    } catch (err) {}
    setIsItemLoaded(true);
  }, [stockMovements, itemId]);

  useEffect(() => {
    fetchItem();
  }, [itemId, fetchItem]);

  // eslint-disable-next-line no-unused-vars
  const handleAssigneeTypeChange = (id, value, isValid) => {
    setCurrentAssigneeType(value);
    setCurrentAssigneeId(null);
    setCurrentAssigneeName("");
    setCurrentVehicleKilometer("");
  };

  // eslint-disable-next-line no-unused-vars
  const handleAssigneeChange = (id, value, isValid) => {
    const personId = value;
    setCurrentAssigneeId(personId);
  };

  // eslint-disable-next-line no-unused-vars
  const handleAssigneeNameChange = (id, value, isValid) => {
    setCurrentAssigneeName(value);
  };

  // eslint-disable-next-line no-unused-vars
  const handleVehicleKilometerChange = (id, value, isValid) => {
    setCurrentVehicleKilometer(value);
  };

  // Dynamic options based on currentAssigneeType
  const assigneeOptions = React.useMemo(() => {
    if (currentAssigneeType === "attendant") {
      return attendants.map((attendant) => ({
        value: attendant.id,
        label: attendant.name,
      }));
    } else if (currentAssigneeType === "driver") {
      return drivers.map((driver) => ({
        value: driver.id,
        label: driver.name,
      }));
    } else if (currentAssigneeType === "vehicle") {
      return vehicleOptions || [];
    } else if (currentAssigneeType === "other") {
      return []; // Empty array for other type - will show text input instead
    }
    return [];
  }, [currentAssigneeType, attendants, drivers, vehicleOptions]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      const infoToBeSent = {
        stock: formState.inputs.stock.value,
        movementType: formState.inputs.movementType.value,
        quantity: parseFloat(formState.inputs.quantity.value),
        description: formState.inputs.description.value,
        referenceNumber: formState.inputs.referenceNumber.value,
        date: formState.inputs.date.value || new Date().toISOString(),
        assigneeType: formState.inputs.movementType.value === "out" ? currentAssigneeType : undefined,
        assigneeId:
          formState.inputs.movementType.value === "out" && currentAssigneeType && currentAssigneeType !== "other"
            ? currentAssigneeId
            : undefined,
        assigneeName:
          formState.inputs.movementType.value === "out" && currentAssigneeType === "other"
            ? currentAssigneeName
            : undefined,
        vehicleKilometer:
          formState.inputs.movementType.value === "out" && currentAssigneeType === "vehicle" && currentVehicleKilometer
            ? parseFloat(currentVehicleKilometer)
            : undefined,
        thirdParty: formState.inputs.thirdParty.value || null,
        cost:
          formState.inputs.cost.value !== "" && formState.inputs.cost.value !== null
            ? parseFloat(formState.inputs.cost.value)
            : null,
      };

      const endpoint = isNewItem
        ? `${process.env.REACT_APP_BACKEND_URL}/stock-movements`
        : `${process.env.REACT_APP_BACKEND_URL}/stock-movements/${itemId}`;

      const postType = isNewItem ? "POST" : "PATCH";

      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent));

      if (responseData?.movement) {
        await RetrieveStocks();
        if (isNewItem) {
          InsertItem(responseData.movement);
          setGeneratedItemId(responseData.movement.id);
        } else {
          UpdateItem(responseData.movement);
          window.toast.success(t("inventoryStockMovement.messages.updateSuccess"));
        }
      }
    } catch (err) {
      console.log(err);
      if (err?.code) {
        window.toast.error(`${t("inventoryStockMovement.messages.error")}: ${err.code}`);
      } else {
        window.toast.error(t("inventoryStockMovement.messages.operationFailed"));
      }
    }
  };

  // Item not found check
  if (isItemLoaded && !isNewItem && !loadedItem && !error) {
    return <NotFound item="stock movement" />;
  }

  // Item generated success page
  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType="stock-movement"
        onGotoNewItem={() => gotoItem && gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose && onClose();
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <DeleteModal
        endPoint="stock-movements"
        itemType="stock-movement"
        itemId={itemId}
        onDeleted={async () => {
          await RetrieveStocks();
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {isItemLoaded && (
        <form className="form-container" onSubmit={handleSubmit}>
          {/* Basic Information Card */}
          <GenericCard style={{ marginBottom: "10px" }}>
            <div className="horizontal-flex">
              <CustomSelect
                id="stock"
                label={t("inventoryStockMovement.fields.stockRequired")}
                options={stockOptions}
                onInput={inputHandler}
                initialValue={{
                  value: loadedItem?.stock || "",
                  label: stockOptions.find((s) => s.value === loadedItem?.stock)?.label || "",
                }}
                disabled={!isNewItem}
              />

              <CustomSelect
                id="movementType"
                label={t("inventoryStockMovement.fields.movementTypeRequired")}
                options={movementTypeOptions}
                onInput={inputHandler}
                initialValue={{
                  value: loadedItem?.movementType || "in",
                  label:
                    loadedItem?.movementType === "out"
                      ? t("inventoryStockMovement.movementType.out")
                      : t("inventoryStockMovement.movementType.in"),
                }}
              />
            </div>

            {/* Assignee fields - only show for out movements */}
            {formState.inputs.movementType.value === "out" && (
              <div className="horizontal-flex">
                <CustomSelect
                  id="assigneeType"
                  label={t("inventoryStockMovement.fields.assignee")}
                  options={[
                    { value: "attendant", label: t("inventoryStockMovement.assigneeTypes.attendant") },
                    { value: "driver", label: t("inventoryStockMovement.assigneeTypes.driver") },
                    { value: "vehicle", label: t("inventoryStockMovement.assigneeTypes.vehicle") },
                    { value: "other", label: t("inventoryStockMovement.assigneeTypes.other") },
                  ]}
                  onInput={handleAssigneeTypeChange}
                  initialValue={{
                    value: currentAssigneeType,
                    label: t(`inventoryStockMovement.assigneeTypes.${currentAssigneeType}`),
                  }}
                />

                {currentAssigneeType === "other" ? (
                  <Input
                    id="assigneeName"
                    element="input"
                    type="text"
                    label={t("inventoryStockMovement.fields.assigneeName")}
                    validators={[VALIDATOR_NONE()]}
                    onInput={handleAssigneeNameChange}
                    initialValue={currentAssigneeName}
                    initialValid={true}
                    placeholder={t("inventoryStockMovement.placeholders.assigneeName")}
                  />
                ) : (
                  <CustomSelect
                    id="assigneeId"
                    key={`assignee-${currentAssigneeType}-${currentAssigneeId}`}
                    label={
                      currentAssigneeType === "vehicle"
                        ? t("inventoryStockMovement.fields.assigneeVehicle")
                        : t("inventoryStockMovement.fields.assigneePersonnel")
                    }
                    options={assigneeOptions}
                    onInput={handleAssigneeChange}
                    initialValue={assigneeOptions.find((option) => option.value === currentAssigneeId) || null}
                    placeholder={
                      currentAssigneeType === "vehicle"
                        ? t("inventoryStockMovement.placeholders.selectVehicle")
                        : t("inventoryStockMovement.placeholders.selectPersonnel")
                    }
                  />
                )}
              </div>
            )}

            {/* Vehicle kilometer field - only show for vehicle out movements */}
            {formState.inputs.movementType.value === "out" && currentAssigneeType === "vehicle" && (
              <div className="horizontal-flex">
                <Input
                  id="vehicleKilometer"
                  element="input"
                  type="number"
                  label={t("inventoryStockMovement.fields.vehicleKilometer")}
                  validators={[VALIDATOR_NONE()]}
                  onInput={handleVehicleKilometerChange}
                  initialValue={currentVehicleKilometer}
                  initialValid={true}
                  placeholder={t("inventoryStockMovement.placeholders.vehicleKilometer")}
                  min="0"
                  step="1"
                />
              </div>
            )}

            <div className="horizontal-flex">
              <Input
                id="quantity"
                element="input"
                type="number"
                label={t("inventoryStockMovement.fields.quantityRequired")}
                validators={[VALIDATOR_REQUIRE()]}
                errorText={t("inventoryStockMovement.errors.quantityRequired")}
                onInput={inputHandler}
                initialValue={loadedItem?.quantity || ""}
                initialValid={!isNewItem}
                min="0.01"
                step="0.01"
              />

              <DatePicker
                id="date"
                label={t("inventoryStockMovement.fields.dateRequired")}
                onInput={inputHandler}
                initialValue={loadedItem?.date || parseComplexDate(new Date())}
                initialValid={!isNewItem}
              />
            </div>

            <div className="horizontal-flex">
              <Input
                id="referenceNumber"
                element="input"
                type="text"
                label={t("inventoryStockMovement.fields.referenceNumber")}
                onInput={inputHandler}
                initialValue={loadedItem?.referenceNumber || ""}
                initialValid={true}
                placeholder={t("inventoryStockMovement.placeholders.referenceNumber")}
                validators={[VALIDATOR_NONE()]}
              />
            </div>

            <div className="horizontal-flex">
              <CustomSelect
                id="thirdParty"
                label={t("inventoryStockMovement.fields.thirdParty")}
                options={thirdPartyOptions}
                onInput={inputHandler}
                initialValue={thirdPartyOptions.find((option) => option.value === loadedItem?.thirdParty) || null}
                initialValid={true}
                placeholder={t("inventoryStockMovement.placeholders.thirdParty")}
                validators={[VALIDATOR_NONE()]}
              />

              <Input
                id="cost"
                element="input"
                type="number"
                label={t("inventoryStockMovement.fields.cost")}
                validators={[VALIDATOR_NONE()]}
                onInput={inputHandler}
                initialValue={
                  loadedItem?.cost !== undefined && loadedItem?.cost !== null ? loadedItem.cost.toString() : ""
                }
                initialValid={true}
                placeholder={t("inventoryStockMovement.placeholders.cost")}
                min="0"
                step="0.01"
              />
            </div>

            <Input
              id="description"
              element="textarea"
              label={t("inventoryStockMovement.fields.description")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.description || ""}
              initialValid={true}
              placeholder={t("inventoryStockMovement.placeholders.description")}
              rows="3"
            />

            {/* Assignee fields - only show for out movements */}
          </GenericCard>

          <FormFooter
            isNewItem={isNewItem}
            onUpdate={handleSubmit}
            onDelete={() => setShowConfirmModal(true)}
            isUpdating={isLoading}
            disabled={
              isLoading ||
              !formState.isValid ||
              !formState.inputs.stock.value ||
              !formState.inputs.movementType.value ||
              !formState.inputs.quantity.value ||
              !formState.inputs.date.value
            }
          />
        </form>
      )}
    </React.Fragment>
  );
};

export default StockMovementItem;
